import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials, stats } from '@/mocks/articles';

export default function About() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormState('success');
      return;
    }

    setFormState('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://sayaworks.github.io/api/form/d9u56rlca23r0tr1aqvg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* not JSON */ }

      if (response.ok && (parsed as { code?: string })?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const serverMsg =
          (parsed as { meta?: { message?: string } })?.meta?.message ||
          (parsed as { message?: string })?.message ||
          (parsed as { meta?: { detail?: string } })?.meta?.detail ||
          responseText ||
          '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormState('error');
    }
  };

  const aboutSchema = [
    {
      '@context': 'https://sayaworks.github.io',
      '@type': 'AboutPage',
      name: '关于我们 — 佐山爱官方资料站',
      description: '了解佐山爱官方资料站的品牌故事、口碑评价与联系方式。',
      url: 'https://sayaworks.github.io/about',
    },
    {
      '@context': 'https://sayaworks.github.io',
      '@type': 'Organization',
      name: '佐山爱官方资料站',
      url: 'https://sayaworks.github.io/',
      description: '佐山爱（Sayama Ai）官方中文资料站，提供个人资料、出道经历、代表作品及最新动态。',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        availableLanguage: ['Chinese'],
      },
    },
    {
      '@context': 'https://sayaworks.github.io',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://sayaworks.github.io/' },
        { '@type': 'ListItem', position: 2, name: '关于我们' },
      ],
    },
  ];

  return (
    <>
      <head>
        <title>关于我们 — 佐山爱官方资料站 | 品牌介绍与联系方式</title>
        <meta name="description" content="了解佐山爱官方资料站的品牌故事、口碑评价与联系方式。我们致力于为中文用户提供最权威、最完整的佐山爱相关信息。" />
        <meta name="keywords" content="佐山爱,关于我们,品牌介绍,联系方式,粉丝平台" />
        <link rel="canonical" href="https://sayaworks.github.io/about" />
        <meta property="og:title" content="关于我们 — 佐山爱官方资料站" />
        <meta property="og:description" content="了解佐山爱官方资料站的品牌故事、口碑评价与联系方式。" />
        <meta property="og:url" content="https://sayaworks.github.io/about" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
        />
      </head>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 md:pt-28">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />

            {/* Hero */}
            <div className="mb-12">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                关于我们
              </h1>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed max-w-2xl">
                佐山爱官方资料站致力于为中文用户提供最权威、最完整的佐山爱相关信息，
                汇聚个人资料、出道经历、代表作品及最新动态，打造温暖而有深度的粉丝交流平台。
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
              {stats.map((stat) => (
                <div key={stat.label} className="bg-background-50 border border-background-200/70 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100/70">
                    <i className={`${stat.icon} text-primary-600 text-lg`}></i>
                  </div>
                  <div className="font-heading text-2xl font-bold text-foreground-950">{stat.value}</div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Brand Introduction */}
            <section className="mb-16">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-6">
                品牌介绍
              </h2>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8 space-y-4">
                <p className="text-sm text-foreground-600 leading-relaxed">
                  佐山爱官方资料站创建于对日本影视文化的热爱与尊敬。我们致力于为中文用户打造一个全面、权威、纯净的信息平台，让每一位对佐山爱感兴趣的观众都能在这里找到所需的内容。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们的团队由一群热爱日本影视文化的资深编辑组成，始终坚持内容的真实性、时效性和完整性。从个人基本资料到最新作品动态，每一个信息点都经过严格的核实与整理，确保呈现给用户的是最准确的内容。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  本站不仅是一个信息聚合平台，更是一个温暖的粉丝社区。我们希望通过这个平台，连接更多喜爱佐山爱的朋友，共同分享关于她的每一个精彩瞬间。无论你是初识佐山爱还是多年的忠实粉丝，这里都欢迎你的加入。
                </p>
              </div>
            </section>

            {/* Reputation */}
            <section className="mb-16">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-6">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {testimonials.map((t) => (
                  <div key={t.id} className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-xs ${i < t.rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-300'}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      &ldquo;{t.content}&rdquo;
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-accent-100/70 flex items-center justify-center">
                        <span className="text-xs font-medium text-accent-700">{t.name[0]}</span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-950">{t.name}</div>
                        <div className="text-xs text-foreground-500">{t.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact Form */}
            <section className="mb-16">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-6">
                联系我们
              </h2>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
                {formState === 'success' ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100/70">
                      <i className="ri-check-line text-2xl text-primary-600"></i>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">消息已发送</h3>
                    <p className="text-sm text-foreground-500">感谢您的留言，我们会尽快回复您。</p>
                    <button
                      onClick={() => setFormState('idle')}
                      className="mt-4 px-5 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      发送新消息
                    </button>
                  </div>
                ) : (
                  <form
                    id="contact-form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="space-y-5"
                  >
                    {formState === 'error' && formError && (
                      <div className="p-4 rounded-lg bg-accent-100/70 border border-accent-200/70 text-sm text-accent-800">
                        {formError}
                      </div>
                    )}

                    {/* Honeypot */}
                    <div className="honeypot-field">
                      <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名 <span className="text-accent-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱 <span className="text-accent-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入消息主题"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容 <span className="text-accent-500">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        rows={5}
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                    </div>

                    <button
                      type="submit"
                      disabled={formState === 'submitting'}
                      className="px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      {formState === 'submitting' ? (
                        <span className="flex items-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i> 发送中...
                        </span>
                      ) : (
                        '发送消息'
                      )}
                    </button>
                  </form>
                )}
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}